<script>
import {defineComponent} from "vue";
import {useRoute, useRouter} from "vue-router";
import LoadingSpinner from "@/javascript/views/components/loadingSpinner.vue";
import HeroBanner from "@/javascript/views/components/landing/HeroBanner.vue";
import InfoSection from "@/javascript/views/components/landing/InfoSection.vue";
import DonatableTargetsList from "../components/landing/DonatableTargetsList.vue";
import GiftBasketSideBarButton from "./GiftBasketSidebarButton.vue";
import {useLandingPageStore} from "../../stores/landingPage";
import {useAnalyticEventStore} from "@/javascript/stores/analyticEvents";
import {useUserStore} from "@/javascript/stores/user";
import DashboardHeader from "@/javascript/views/components/dashboard/DashboardHeader.vue";
import Icon from "@/javascript/views/components/Icon.vue";
import PageArticle from "../components/landing/pageArticle.vue";
import VideoSection from "../components/landing/VideoSection.vue";


export default defineComponent({
  name: "LandingPage",
  components: {
    VideoSection,
    PageArticle,
    GiftBasketSideBarButton,
    InfoSection,
    LoadingSpinner,
    HeroBanner,
    DonatableTargetsList,
    DashboardHeader,
    Icon
  },
  props: {},
  setup() {
    const route = useRoute();
    const router = useRouter();
    const landingPageStore = useLandingPageStore();
    const analyticEventStore = useAnalyticEventStore();
    const userStore = useUserStore();

    return {
      landingPageStore,
      analyticEventStore,
      route,
      router,
      userStore
    };
  },
  data() {
    return {
      customization: null,
      showDonationModal: false,
      loading: false,
      hasNavigatedToLanding: false,
    };
  },
  computed: {
    pageData() {
      return this.landingPageStore.publishedLayout?.pages?.find((page) => page.page_type === 'landing_page');
    },
    orderedComponents() {
      return this.pageData?.components?.sort((a, b) => a.order - b.order).filter(c => !c.archived);
    },
    organizationId() {
      return this.landingPageStore.organizationId;
    },
    campaigns() {
      return this.landingPageStore.campaigns;
    },

    organization() {
      return this.landingPageStore.organization;
    },
    brand() {
      return this.landingPageStore.localBrand;
    },
    isLoggedIn() {
      // If we’ve already loaded a user, use that user otherwise fall back
      return !!(this.userStore.user || document.body.dataset.currentUserId);
    },
  },
  watch: {},
  mounted() {
    if (this.organizationId) {
      this.analyticEventStore.setOrganizationId(this.organizationId);
    }

    // Check if the URL contains source=qr query parameter
    if (this.route.query.source === 'qr') {
      const url = window.location.href;

      // Log the event
      this.analyticEventStore.logEvent({
        action: `landingpage-qr-visit`,
        metadata: {
          timestamp: new Date().toISOString(),
          organization: this.organization,
          destination: url,
          source: 'QR Code',
          donatable_type: 'LandingPage'
        }
      });
    }
  },
  methods: {
    componentValues(component) {
      return this.landingPageStore.getComponentValues(component);
    },
  }
});

</script>

<template>
  <div
    v-if="organization === null || loading"
    class="loading-spinner-container"
  >
    <LoadingSpinner />
  </div>
  <div
    v-else
    class="landing-page-container"
  >
    <div v-for="component in orderedComponents">
      <hero-banner
        v-if="component.component_type === 'hero_banner'"
        v-bind="componentValues(component)"
      />

      <video-section
        v-if="component.component_type === 'video'"
        :title="component.component_data.title?.value"
        :subtitle="component.component_data.subtitle?.value"
        :video="component.component_data.video?.value"
        :autoplay="component.component_data.autoplay?.value"
        :muted="component.component_data.muted?.value"
      />

      <page-article
        v-if="component.component_type === 'article'"
        v-bind="componentValues(component)"
      />
      <donatable-targets-list
        v-if="component.component_type === 'donatable_grid'"
        v-bind="componentValues(component)"
        :key="landingPageStore.donationTargets?.length"
        :donation-targets="landingPageStore.donationTargets"
        :organization="organization"
        :primary-color="brand.color_primary"
        :primary-dark-color="brand.color_primary_dark"
        :primary-text-color="landingPageStore.getContrastTextColor(brand.color_primary)"
        :landing-page="true"
        :show-short-description="true"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import '@/styles/_variables.scss';

.loading-spinner-container {
  width: 100vw;
  height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;

}

.landing-page-container {
  max-width: 100dvw;
  overflow-x: hidden;
  background-color: $color-section-bg;
  background-color: $color-section-bg;
  // setup container widths
  .outer-container {
    background-color: $color-section-bg;
    width: 100%;
    display: flex;
    justify-content: center;

    :deep(.inner-container) {
      width: 100%;
      max-width: $content-max-width;


      /** Media Queries */

      // default (smallest screen)
      padding: $space-md $space-lg;
      margin: $space-md 0;

      // tablet screens
      @media screen and (min-width: $breakpoint-min-sm) and (max-width: $breakpoint-max-lg) {
        margin: $space-md;
        padding: $space-lg !important;

      }
      // desktop
      @media screen and (min-width: $breakpoint-max-lg) {
        margin: $space-md $space-lg;
        padding: $space-xl;
      }
    }
  }

  :deep(.header) {
    background: transparent !important;
    box-shadow: none !important;
    height: 64px !important; /* match default header height */
    display: flex;
    align-items: center;
  }

  :deep(.header-container) {
    height: 64px !important; /* match header height */
    display: flex;
    align-items: center;
    padding-right: 0;
  }

  :deep(.hamburger-btn) {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 40px;
    width: 40px;
  }

  :deep(.menu-section) {
    margin-right: 0 !important;
  }

  :deep(.menu-section .dropdown-menu) {
    z-index: 1000;
  }

  /* Kill the big left block reserved */
  .compact-hamburger :deep(.logo-container),
  .compact-hamburger :deep(.title-medium) {
    display: none !important;
    width: 0 !important;
    padding: 0 !important;
  }

}
</style>
